/**
 * Extern Labs PUBLIC PREVIEW, Experiment 4's event vocabulary.
 *
 * Experiment 4 puts a shortened Labs sprint on a public page linked from the pSEO internship guides.
 * It runs in the SAME Mixpanel project as Experiment 3 (the intent overlay, enrolled 2026-08-24), which
 * is the reason this file exists at all.
 *
 * 🔴 NOT ONE STRING HERE MAY APPEAR IN ANY `*_discovery_*` EVENT. The three vertical event files are
 * the Experiment 3 vocabulary; every one of their names is `{vertical}_discovery_*`, and every board
 * card and funnel is keyed on those literals. This file is uniformly `labs_preview_*`, so the two
 * populations cannot be pooled by accident even by a careless funnel. An Experiment 4 event turning up
 * inside an Experiment 3 denominator is a bug, not a cohort.
 *
 * The two surfaces are ALSO physically disjoint, which is the belt to this file's braces: the preview
 * lives on a public route outside the paywall, its readers are signed out, and it never reads the Labs
 * arm flag. So a preview reader cannot be enrolled in Experiment 3, and an Experiment 3 learner cannot
 * emit these events.
 *
 * WHAT THIS EXPERIMENT MEASURES. Round one's headline is EMAIL CAPTURED, with accounts as the lagging
 * read (Jay, 2026-08-28, after Adam's "you cannot get people to a trial until you are good at capturing
 * their email"). The chain that matters is VIEWED → COMPLETED → ARTIFACT_SHOWN → ARTIFACT_UNLOCKED,
 * each over the one before it. SIGNUP_HANDOFF is the round-two number and is expected to be small and
 * slow; do not judge round one by it.
 *
 * `guide_source` and `entry` are on every event, so the whole funnel reads per guide page and per entry
 * path (a plain link versus the in-page question block) rather than only in aggregate.
 */
export const LABS_PREVIEW_EVENTS = {
  /**
   * The page was opened. The DENOMINATOR for everything else, and deliberately separate from the
   * platform's automatic pageview so it carries `vertical` and `guide_source` from the first beat.
   */
  VIEWED: 'labs_preview_viewed',
  /** The reader started the lab (left the welcome screen). VIEWED minus this is the cold bounce. */
  STARTED: 'labs_preview_started',
  /**
   * Per-step progress, first time each step is reached. Shares the sprint's STEP IDS (see
   * `preview-steps.ts`) so abandon points are comparable across the two surfaces, while the EVENT name
   * stays disjoint.
   */
  STEP_VIEWED: 'labs_preview_step_viewed',
  MODULE_COMPLETED: 'labs_preview_module_completed',
  ARCHETYPE_REVEALED: 'labs_preview_archetype_revealed',
  /** Reached the payoff. This is "completed the lab" and the denominator for the email ask. */
  COMPLETED: 'labs_preview_completed',
  /** The email field was submitted. Over COMPLETED = how many finishers will give an address. */
  EMAIL_SUBMITTED: 'labs_preview_email_submitted',
  /**
   * The reader was handed to sign-up. The last event this surface owns. Whether they then finish
   * sign-up is answered by the platform's own auth events, joined on email.
   *
   * Carries `path`: 'externship_card' (a matched program), or 'artifact' (the CTA in the unlocked
   * takeaway panel). The board has to tell them apart, because they answer different questions. If
   * every signup arrives via a program card, the takeaway earns addresses but not accounts, and the
   * email-first framing of round one needs revisiting.
   */
  SIGNUP_HANDOFF: 'labs_preview_signup_handoff',
  /** A matched externship card was clicked from the payoff. */
  RECOMMENDATION_CLICKED: 'labs_preview_recommendation_clicked',
  /**
   * The takeaway artifact was offered, still locked. The denominator for the email ask.
   *
   * Deliberately separate from COMPLETED: a reader can finish the lab and never scroll to the artifact,
   * and telling those two apart is the difference between "the ask is weak" and "the ask is unseen".
   */
  ARTIFACT_SHOWN: 'labs_preview_artifact_shown',
  /**
   * An email was given and the artifact unlocked. ARTIFACT_UNLOCKED over ARTIFACT_SHOWN is the read on
   * whether a takeaway is a better email hook than the old "we will send you your result" ask, which is
   * the whole bet behind building it.
   */
  ARTIFACT_UNLOCKED: 'labs_preview_artifact_unlocked',
  /**
   * The file was actually taken. Unlocking without downloading means the artifact is a price they paid
   * rather than a thing they wanted, which is the failure mode worth watching for.
   */
  ARTIFACT_DOWNLOADED: 'labs_preview_artifact_downloaded',
} as const;

export type LabsPreviewEventName = (typeof LABS_PREVIEW_EVENTS)[keyof typeof LABS_PREVIEW_EVENTS];
